import React, { FC, useCallback, useEffect, useRef, useState } from "react"
import Modal from "@zambezi/sdk/modal"
import { useIdleTimer } from "react-idle-timer"
import { format, addMilliseconds } from "date-fns"
import { InactivityPromptProps } from "./InactivityPrompt.types"

import "./InactivityPrompt.scss"

const InactivityPrompt: FC<InactivityPromptProps> = ({
    timeout,
    promptTimeout: promptTimeoutOptional,
    onTimeout,
    title,
    confirm,
    onConfirm,
    cancel,
    onCancel,
    children,
    className,
}: InactivityPromptProps) => {
    const promptTimeout =
        promptTimeoutOptional !== undefined && !isNaN(promptTimeoutOptional)
            ? promptTimeoutOptional
            : 1000 * 60 * 2

    if (timeout === undefined || isNaN(timeout)) timeout = 1000 * 60 * 20

    timeout -= promptTimeout

    const [showPrompt, setShowPrompt] = useState(false)
    const [timeRemaining, setTimeRemaining] = useState(promptTimeout)
    const promptCountdown = useRef<number | undefined>()
    const promptTimer = useRef<number | undefined>()
    const timerEnd = useRef<number | undefined>()

    const doCountdown = useCallback(() => {
        const now = new Date().getTime()
        const t = (timerEnd.current ?? 0) - now
        setTimeRemaining(Math.max(Math.round(t / 1000) * 1000, 0))
    }, [])

    const klass = `zb-inactivity-prompt ${className || ""}`

    const idleTimer = useIdleTimer({
        timeout: timeout,
        onIdle: useCallback(() => {
            setTimeRemaining(promptTimeout)
            setShowPrompt(true)
        }, [setShowPrompt, setTimeRemaining, promptTimeout]),
    })

    /**
     * Formats the remaining duration into our preferred display format
     *
     * @param {number} duration The time duration to format
     * @returns {string} The formatted time
     */
    function formatDuration(duration: number): string {
        return format(duration, "mm:ss")
    }

    /** Clears any remaining timers */
    function clearTimers() {
        window.clearTimeout(promptCountdown.current)
        window.clearInterval(promptTimer.current)
    }

    /** Clears the prompt and resets all timers */
    function resume() {
        clearTimers()
        idleTimer.reset()
        setShowPrompt(false)
    }

    useEffect(() => {
        if (showPrompt) {
            promptCountdown.current = window.setTimeout(() => {
                clearTimers()
                setShowPrompt(false)
                onTimeout && onTimeout()
            }, promptTimeout)

            timerEnd.current = addMilliseconds(
                new Date(),
                promptTimeout
            ).getTime()
            promptTimer.current = window.setInterval(doCountdown, 1000)
        } else {
            clearTimers()
        }

        return () => {
            clearTimers()
        }
    }, [showPrompt, doCountdown, onTimeout, setTimeRemaining, promptTimeout])

    return (
        <Modal
            className={klass}
            title={title ?? "You'll be timed out due to inactivity"}
            open={showPrompt}
            confirm={confirm ?? "Continue session"}
            cancel={cancel}
            onConfirm={() => {
                resume()
                onConfirm && onConfirm()
            }}
            onCancel={() => {
                resume()
                onCancel && onCancel()
            }}
        >
            {children ? (
                <>{children}</>
            ) : (
                <p>
                    For security reasons you will be signed out if you remain
                    inactive.
                </p>
            )}

            <p>
                <b>Time out in {formatDuration(timeRemaining)}</b>
            </p>
        </Modal>
    )
}

InactivityPrompt.defaultProps = {
    timeout: parseInt(process.env.REACT_APP_INACTIVITY_TIMEOUT ?? ""),
    promptTimeout: parseInt(process.env.REACT_APP_INACTIVITY_PROMPT ?? ""),
}

export default InactivityPrompt
